import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Mail, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp, 
  Building2,
  Calculator,
  PieChart,
  Award,
  XCircle,
  ArrowRight,
  Briefcase,
  Target,
  Shield,
  Clock,
  Users,
  ChevronRight
} from 'lucide-react';

export default function ConvaincreBanquier() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '30px 30px'}}></div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-28">
          <div className="text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium mb-6">
              Guide Investisseur Immobilier
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Comment <span className="text-amber-400">convaincre</span> votre banquier<br />
              de vous financer à 110% ?
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-10">
              La différence entre un investisseur qui obtient son financement et celui qui essuie un refus 
              tient souvent à une seule chose : <strong className="text-white">la qualité du dossier présenté.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                Créer mon dossier professionnel
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "73%", label: "des dossiers refusés sont incomplets" },
              { value: "110%", label: "financement possible avec un bon dossier" },
              { value: "15min", label: "temps moyen d'analyse par le banquier" },
              { value: "3x", label: "plus de chances avec un dossier structuré" }
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Le problème : L'investisseur désorganisé */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-red-100 text-red-600 text-sm font-medium mb-4">
              Le problème
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              L'investisseur <span className="text-red-500">désorganisé</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Voici ce que voit un conseiller bancaire chaque jour... et pourquoi il refuse 7 dossiers sur 10.
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 sm:p-12 border border-red-100">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Left: Email chaos illustration */}
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border-l-4 border-red-400">
                  <Mail className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Email 1 : "Bonjour, je cherche un prêt"</div>
                    <div className="text-sm text-slate-500">Pas de détails, pas de chiffres, pas de projet clair...</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border-l-4 border-red-400">
                  <Mail className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Email 5 : "J'ai oublié de vous dire..."</div>
                    <div className="text-sm text-slate-500">Informations au compte-gouttes, le banquier perd le fil.</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border-l-4 border-red-400">
                  <Mail className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Email 12 : "Voici les derniers documents"</div>
                    <div className="text-sm text-slate-500">15 pièces jointes en vrac, formats différents, noms incompréhensibles...</div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border-l-4 border-orange-400">
                  <AlertTriangle className="w-8 h-8 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Le banquier : "Dossier incomplet"</div>
                    <div className="text-sm text-slate-500">Trop de travail pour reconstituer le puzzle. Refus.</div>
                  </div>
                </div>
              </div>

              {/* Right: Problems list */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-red-500" />
                  Ce qui tue votre dossier
                </h3>
                
                {[
                  { title: "Aucune vision globale", desc: "Le banquier ne comprend pas votre projet en 5 minutes" },
                  { title: "Calculs approximatifs", desc: "\"Je pense que ça va être rentable\" n'est pas un argument" },
                  { title: "Charges oubliées", desc: "Taxe foncière, assurances, copropriété, vacance locative..." },
                  { title: "Pas de comparaison marché", desc: "Comment justifier votre loyer prévisionnel ?" },
                  { title: "Documents éparpillés", desc: "Le banquier n'a pas le temps de faire le tri" },
                  { title: "Fiscalité ignorée", desc: "LMNP, micro-foncier, réel... Quel impact sur votre cash-flow ?" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white/80 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-900">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <div className="mt-10 p-6 bg-white rounded-2xl border border-red-200">
              <blockquote className="text-lg italic text-slate-700">
                "Quand je reçois un dossier en 15 emails avec des pièces jointes nommées 'scan123.pdf', 
                je sais déjà que l'investisseur n'est pas sérieux. Si vous ne savez pas présenter votre projet, 
                comment puis-je vous faire confiance pour gérer un bien immobilier ?"
              </blockquote>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">Conseiller bancaire</div>
                  <div className="text-sm text-slate-500">Grande banque française, 12 ans d'expérience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* La solution : L'investisseur professionnel */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 text-emerald-600 text-sm font-medium mb-4">
              La solution
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              L'investisseur <span className="text-emerald-500">professionnel</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un seul document, une vision claire, des chiffres vérifiables. 
              Voici comment vous démarquer et obtenir votre financement.
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 sm:p-12 border border-emerald-100">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Left: Success elements */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  Un dossier qui inspire confiance
                </h3>
                
                {[
                  { 
                    icon: FileText,
                    title: "Un document unique et complet", 
                    desc: "Toutes les informations en un seul PDF professionnel" 
                  },
                  { 
                    icon: Calculator,
                    title: "Calculs détaillés et vérifiables", 
                    desc: "Rendement, cash-flow, TRI... Tout est chiffré précisément" 
                  },
                  { 
                    icon: PieChart,
                    title: "Toutes les charges anticipées", 
                    desc: "Rien n'est oublié : taxe foncière, assurances, gestion, travaux..." 
                  },
                  { 
                    icon: TrendingUp,
                    title: "Comparaison avec le marché", 
                    desc: "Données réelles Airbnb, SeLoger, INSEE pour justifier vos hypothèses" 
                  },
                  { 
                    icon: Shield,
                    title: "Fiscalité optimisée", 
                    desc: "Impact fiscal simulé selon votre régime (LMNP, micro, réel)" 
                  },
                  { 
                    icon: Building2,
                    title: "Projection sur 20 ans", 
                    desc: "Évolution du patrimoine, amortissement, stratégie de sortie" 
                  }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white/80 rounded-lg">
                    <item.icon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-900">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: Benefits */}
              <div className="space-y-6">
                <div className="p-6 bg-white rounded-2xl shadow-sm border border-emerald-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Target className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Financement à 110%</h4>
                  </div>
                  <p className="text-slate-600">
                    Avec un dossier complet, le banquier peut financer non seulement le bien, 
                    mais aussi les frais de notaire et les travaux. <strong>Zéro apport nécessaire.</strong>
                  </p>
                </div>

                <div className="p-6 bg-white rounded-2xl shadow-sm border border-emerald-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Réponse rapide</h4>
                  </div>
                  <p className="text-slate-600">
                    Un dossier clair et complet = une analyse rapide. 
                    Le banquier peut prendre sa décision en quelques jours, pas en quelques semaines.
                  </p>
                </div>

                <div className="p-6 bg-white rounded-2xl shadow-sm border border-emerald-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg">Relation de confiance</h4>
                  </div>
                  <p className="text-slate-600">
                    Plus vous apportez de dossiers professionnels à votre banquier, 
                    plus il vous fera confiance pour vos prochains investissements.
                  </p>
                </div>
              </div>
            </div>

            {/* Success quote */}
            <div className="mt-10 p-6 bg-white rounded-2xl border border-emerald-200">
              <blockquote className="text-lg italic text-slate-700">
                "Quand un client arrive avec un dossier comme ça, je sais que je suis face à quelqu'un de sérieux. 
                Il a fait ses devoirs, il connaît son projet sur le bout des doigts. 
                C'est le genre de client que je veux accompagner sur le long terme."
              </blockquote>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">Directeur d'agence bancaire</div>
                  <div className="text-sm text-slate-500">Spécialisé en financement immobilier</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparaison côte à côte */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              La différence est <span className="text-amber-500">flagrante</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Amateur */}
            <div className="bg-white rounded-3xl p-8 border-2 border-red-200 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-slate-900">L'approche amateur</h3>
                  <p className="text-red-500 text-sm font-medium">Taux de succès : ~20%</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                {[
                  "15 emails échangés sur 3 semaines",
                  "Pièces jointes en vrac (scan001.pdf...)",
                  "Calculs sur un coin de nappe",
                  "\"Je pense que ça va être rentable\"",
                  "Charges majeures oubliées",
                  "Aucune donnée marché",
                  "Fiscalité : \"On verra plus tard\"",
                  "Banquier : perte de temps, refus"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-6 p-4 bg-red-50 rounded-xl">
                <div className="text-red-600 font-semibold">Résultat probable :</div>
                <div className="text-slate-700">Refus ou financement partiel à taux élevé</div>
              </div>
            </div>

            {/* Pro */}
            <div className="bg-white rounded-3xl p-8 border-2 border-emerald-200 shadow-lg relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full">
                RECOMMANDÉ
              </div>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-slate-900">L'approche professionnelle</h3>
                  <p className="text-emerald-500 text-sm font-medium">Taux de succès : ~80%</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                {[
                  "1 email avec 1 dossier PDF complet",
                  "Document structuré et professionnel",
                  "Calculs détaillés et vérifiables",
                  "Rendement, cash-flow, TRI chiffrés",
                  "Toutes les charges anticipées",
                  "Comparaison marché (Airbnb, SeLoger)",
                  "Simulation fiscale intégrée",
                  "Banquier : confiance, accord rapide"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-6 p-4 bg-emerald-50 rounded-xl">
                <div className="text-emerald-600 font-semibold">Résultat probable :</div>
                <div className="text-slate-700">Financement 100-110% à taux négocié</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Créez votre dossier bancaire professionnel<br />
            <span className="text-amber-400">en quelques minutes</span>
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Achat Immo Gagnant génère automatiquement un dossier complet avec tous les calculs, 
            comparaisons marché et projections dont votre banquier a besoin.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Calculator, title: "Simulation complète", desc: "10 étapes pour ne rien oublier" },
              { icon: PieChart, title: "6 graphiques", desc: "Visualisation claire pour le banquier" },
              { icon: FileText, title: "PDF professionnel", desc: "Prêt à envoyer en 1 clic" }
            ].map((item, i) => (
              <div key={i} className="p-6 bg-white/10 rounded-2xl backdrop-blur">
                <item.icon className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <div className="font-semibold text-white mb-1">{item.title}</div>
                <div className="text-sm text-slate-400">{item.desc}</div>
              </div>
            ))}
          </div>

          <button 
            onClick={() => navigate('/')}
            className="px-10 py-5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-lg rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 mx-auto"
          >
            Commencer ma simulation
            <ChevronRight className="w-6 h-6" />
          </button>
          
          <p className="mt-6 text-slate-400">
            <strong className="text-white">4,99€</strong> pour un dossier complet • Satisfait ou remboursé
          </p>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="py-8 px-4 bg-slate-900 text-center">
        <button 
          onClick={() => navigate('/')}
          className="text-slate-400 hover:text-white transition-colors"
        >
          ← Retour à l'accueil
        </button>
      </footer>
    </div>
  );
}
